/**
 * Location Selector Component
 *
 * Shared dropdown that allows users to select which location's data to view.
 * Used in portal headers to filter data by location context.
 * When a factory location is selected, the factory context is also set.
 *
 * @example
 * import { LocationSelector } from '@ufc/domains/locations'
 * <LocationSelector />
 */

import { useCallback, useEffect, useState, useMemo } from 'react'
import { useTranslation, useSelector, useDispatch } from '@ufc/core'
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  Badge,
  cn,
  MapPin,
  Check,
  ChevronsUpDown,
} from '@ufc/uikit'
import {
  selectCurrentLocation,
  selectLocationsList,
  selectLocationDisplayName,
} from '../../store/locations.selectors'
import { locationsActions } from '../../store/locations.slice'
import type { LocationSummary } from '../../domain/types'
import { LOCATION_TYPE_CODES } from '../../domain/constants'

// ============================================
// Constants
// ============================================

const TYPE_CONFIG: Record<string, { labelKey: string; bgClass: string }> = {
  [LOCATION_TYPE_CODES.FACTORY_LOCATION]: {
    labelKey: 'locations.type.factory',
    bgClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  [LOCATION_TYPE_CODES.OFFICE_LOCATION]: {
    labelKey: 'locations.type.office',
    bgClass: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  },
}

// ============================================
// Helper Functions
// ============================================

function filterLocationsBySearch(locations: LocationSummary[], search: string): LocationSummary[] {
  if (!search.trim()) return locations
  const term = search.toLowerCase()
  return locations.filter(
    (l) =>
      l.name.toLowerCase().includes(term) ||
      l.code.toLowerCase().includes(term) ||
      (l.factoryName?.toLowerCase().includes(term) ?? false)
  )
}

function filterLocationsByAllowedTypes(
  locations: LocationSummary[],
  allowedCodes: readonly string[] | undefined
): LocationSummary[] {
  if (!allowedCodes?.length) return locations
  const allowed = new Set(allowedCodes)
  return locations.filter((l) => {
    const code = l.locationType?.code
    return code !== undefined && allowed.has(code)
  })
}

function getTypeConfig(typeCode: string | undefined): { labelKey: string; bgClass: string } {
  if (!typeCode) return { labelKey: 'locations.type.unknown', bgClass: 'bg-gray-100 text-gray-700' }
  return TYPE_CONFIG[typeCode] ?? { labelKey: 'locations.type.unknown', bgClass: 'bg-gray-100 text-gray-700' }
}

function getLocationOptionLabel(location: LocationSummary): string {
  const isFactoryLocation =
    location.locationType?.code === LOCATION_TYPE_CODES.FACTORY_LOCATION

  if (isFactoryLocation && location.factoryName) {
    return location.factoryName
  }

  return location.name
}

// ============================================
// Component
// ============================================

export interface LocationSelectorProps {
  /**
   * When set, only locations whose `locationType.code` is in this list appear in the dropdown.
   * Omit to show every location from the store (default portal behavior).
   */
  allowedLocationTypeCodes?: readonly string[]
}

export function LocationSelector({ allowedLocationTypeCodes }: LocationSelectorProps = {}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const selectedLocation = useSelector(selectCurrentLocation)
  const locationsList = useSelector(selectLocationsList)
  const displayName = useSelector(selectLocationDisplayName)

  useEffect(() => {
    if (!allowedLocationTypeCodes?.length) return
    const code = selectedLocation?.locationType?.code
    if (!selectedLocation || !code) return
    if (!allowedLocationTypeCodes.includes(code)) {
      dispatch(locationsActions.clearLocationSelection())
    }
  }, [allowedLocationTypeCodes, selectedLocation, dispatch])

  const locationsForSelector = useMemo(
    () => filterLocationsByAllowedTypes(locationsList, allowedLocationTypeCodes),
    [locationsList, allowedLocationTypeCodes]
  )

  const filteredLocations = useMemo(
    () => filterLocationsBySearch(locationsForSelector, search),
    [locationsForSelector, search]
  )

  const handleSelectLocation = useCallback(
    (location: LocationSummary) => {
      if (selectedLocation?.documentId === location.documentId) {
        setOpen(false)
        return
      }
      dispatch(locationsActions.selectLocation(location))
      setOpen(false)
    },
    [dispatch, selectedLocation]
  )

  const handleClearSelection = useCallback(() => {
    if (!selectedLocation) {
      setOpen(false)
      return
    }
    dispatch(locationsActions.clearLocationSelection())
    setOpen(false)
  }, [dispatch, selectedLocation])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between"
        >
          <div className="flex items-center gap-2 truncate">
            <MapPin className="h-4 w-4 shrink-0 opacity-70" />
            <span className="truncate">{displayName || t('header.selectLocation')}</span>
          </div>
          <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="end">
        <Command>
          <CommandInput
            placeholder={t('header.searchLocations')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{t('common.noResults')}</CommandEmpty>
            <CommandGroup>
              {/* All Locations option */}
              <CommandItem
                value="all-locations"
                onSelect={handleClearSelection}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 opacity-70" />
                  <span>{t('header.allLocations')}</span>
                </div>
                {!selectedLocation && <Check className="h-4 w-4" />}
              </CommandItem>

              {/* Divider */}
              <div className="my-1 h-px bg-border" />

              {/* Location list */}
              {filteredLocations.map((location) => {
                const typeConfig = getTypeConfig(location.locationType?.code)
                return (
                  <CommandItem
                    key={location.documentId}
                    value={getLocationOptionLabel(location)}
                    onSelect={() => handleSelectLocation(location)}
                    className="flex items-center justify-between"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">{getLocationOptionLabel(location)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="secondary"
                        className={cn('text-xs', typeConfig.bgClass)}
                      >
                        {t(typeConfig.labelKey)}
                      </Badge>
                      {selectedLocation?.documentId === location.documentId && (
                        <Check className="h-4 w-4" />
                      )}
                    </div>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
